import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import type { AnnouncementDto } from '@mesterplan/api-client';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useWhatsNewPrompt } from '@/hooks/useWhatsNewPrompt';
import { ScreenshotLightbox, type ScreenshotLightboxImage } from './ScreenshotLightbox';
import { WhatsNewNoteCard } from './WhatsNewNoteCard';
import { newsCountLabel } from './whatsNewFormat';
import { WHATS_NEW_PATH } from './WhatsNewSidebarRow';

/**
 * The launch prompt: once per release, the first time Mesterplan opens after an update. Mounted in
 * the admin and supplier layouts under `idpEnabled`; `useWhatsNewPrompt` decides when it opens.
 *
 * Centred at 560 px on desktop (wider than the Dialog's `max-w-lg`, so the screenshot can be read).
 * Below 600 px it is a full-width bottom sheet with thumb-reach buttons. That variant is local
 * `className` overrides on `DialogContent` (media-query classes), so no other dialog changes.
 * Luk marks the shown notes read; the opt-out link only hides this dialog (row, page and badge stay).
 */
export function WhatsNewLaunchDialog() {
  const prompt = useWhatsNewPrompt();
  const navigate = useNavigate();
  const [zoomed, setZoomed] = useState<ScreenshotLightboxImage | null>(null);

  const handleZoom = useCallback((item: AnnouncementDto) => {
    if (item.image) setZoomed({ url: item.image.url, alt: item.image.alt, title: item.title });
  }, []);
  const closeLightbox = useCallback(() => setZoomed(null), []);

  if (!prompt.visible) return null;

  const { close, optOut } = prompt;
  return (
    <>
      <Dialog open onOpenChange={(next) => !next && void close()}>
        <DialogContent
          role="dialog"
          aria-labelledby="whats-new-launch-title"
          className="max-w-[560px] rounded-xl max-[599px]:fixed max-[599px]:inset-x-0 max-[599px]:bottom-0 max-[599px]:max-h-[86vh] max-[599px]:max-w-none max-[599px]:rounded-b-none max-[599px]:rounded-t-[24px]"
        >
          <DialogHeader onClose={() => void close()} className="max-[599px]:px-5 max-[599px]:py-[18px]">
            <DialogTitle id="whats-new-launch-title" className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary dark:bg-primary-900/30 dark:text-primary-400">
                <Sparkles className="h-5 w-5" aria-hidden="true" />
              </span>
              <span>
                Nyt i Mesterplan
                <span className="block text-[13px] font-normal leading-[18px] text-muted dark:text-dark-muted">
                  {newsCountLabel(prompt.totalUnread)} siden dit sidste besøg
                </span>
              </span>
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="px-6 py-5 max-[599px]:px-5 max-[599px]:py-3.5">
            {prompt.items.map((item) => (
              <WhatsNewNoteCard key={item.id} item={item} compact showUnread showDate onZoom={handleZoom} />
            ))}
          </DialogBody>
          <DialogFooter className="max-[599px]:flex-col-reverse max-[599px]:items-stretch max-[599px]:gap-2.5 max-[599px]:px-5 max-[599px]:pb-[22px] max-[599px]:pt-4">
            <button
              type="button"
              onClick={() => void optOut()}
              className="mr-auto text-[13px] text-muted underline underline-offset-[3px] hover:text-dark dark:text-dark-muted dark:hover:text-dark-text max-[599px]:mr-0 max-[599px]:mt-1 max-[599px]:self-center"
            >
              Vis ikke nyheder automatisk
            </button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void close();
                navigate(WHATS_NEW_PATH);
              }}
            >
              Se alle nyheder
            </Button>
            <Button type="button" onClick={() => void close()}>
              Luk
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ScreenshotLightbox image={zoomed} onClose={closeLightbox} />
    </>
  );
}
